import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { api, money, TYPE_LABELS } from "../../lib/api";
import { useTheme } from "../../context/AppContext";

const PALETTE = {
  dark: { gold: "#D4AF37", green: "#2D5A41", text: "#A1A5B0", grid: "rgba(255,255,255,0.06)", tip: "#1A1C20", types: ["#D4AF37", "#2D5A41", "#E8DCC0", "#6FBF8E"] },
  light: { gold: "#966F1E", green: "#1E4731", text: "#525760", grid: "rgba(0,0,0,0.06)", tip: "#FFFFFF", types: ["#966F1E", "#1E4731", "#C9B88F", "#3E7A57"] },
};

const Panel = ({ title, children, testid, className = "" }) => (
  <div className={`wb-card p-6 ${className}`} data-testid={testid}>
    <div className="eyebrow mb-5">{title}</div>
    {children}
  </div>
);

function TopList({ rows, metric, testid }) {
  const max = Math.max(1, ...rows.map((r) => r[metric]));
  if (!rows.length) return <p className="text-sm muted">No sales yet.</p>;
  return (
    <div className="space-y-4" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={r.product_id}>
          <div className="flex justify-between gap-4 text-sm">
            <span className="truncate"><span className="font-mono text-xs muted mr-2">{String(i + 1).padStart(2, "0")}</span>{r.title}</span>
            <span className="font-mono shrink-0">{metric === "revenue" ? money(r.revenue) : `${r.units} sold`}</span>
          </div>
          <div className="mt-1.5 h-1 bg-wb-bg2"><div className="h-1 bg-wb-gold transition-all duration-700" style={{ width: `${(r[metric] / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export function SalesCharts() {
  const { theme } = useTheme();
  const c = PALETTE[theme] || PALETTE.dark;
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)).catch(() => setFailed(true)); }, []);
  if (failed) return <p className="mt-12 text-sm text-red-400" data-testid="charts-error">Sales charts could not be loaded.</p>;
  if (!d) return null;
  const axis = { stroke: c.text, fontSize: 11, tickLine: false, axisLine: false };
  const tip = { contentStyle: { background: c.tip, border: `1px solid ${c.grid}`, borderRadius: 0, fontSize: 12 }, cursor: { fill: c.grid } };
  const types = d.by_type.map((t) => ({ ...t, name: TYPE_LABELS[t.type] }));
  return (
    <div className="mt-12 space-y-8" data-testid="sales-charts">
      <div className="grid gap-8 xl:grid-cols-3">
        <Panel title="Monthly revenue · last 12 months (USD)" testid="chart-monthly-revenue" className="xl:col-span-2">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={d.monthly}>
                <CartesianGrid vertical={false} stroke={c.grid} />
                <XAxis dataKey="label" {...axis} />
                <YAxis {...axis} tickFormatter={(v) => `$${v}`} width={60} />
                <Tooltip {...tip} formatter={(v) => [money(v), "Revenue"]} />
                <Bar dataKey="revenue" fill={c.gold} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Revenue by product type" testid="chart-by-type">
          {types.length === 0 ? <p className="text-sm muted">No sales yet.</p> : (
            <>
              <div className="h-52">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={types} dataKey="revenue" nameKey="name" innerRadius={55} outerRadius={85} stroke="none" paddingAngle={2}>
                      {types.map((t, i) => <Cell key={t.type} fill={c.types[i % c.types.length]} />)}
                    </Pie>
                    <Tooltip {...tip} formatter={(v) => money(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-2 text-xs">
                {types.map((t, i) => (
                  <div key={t.type} className="flex items-center justify-between" data-testid={`by-type-${t.type}`}>
                    <span className="flex items-center gap-2"><span className="h-2.5 w-2.5" style={{ background: c.types[i % c.types.length] }} />{t.name}</span>
                    <span className="font-mono">{money(t.revenue)} · {t.units}u</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Panel>
      </div>
      <div className="grid gap-8 xl:grid-cols-3">
        <Panel title="Orders per month" testid="chart-monthly-orders">
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={d.monthly}>
                <CartesianGrid vertical={false} stroke={c.grid} />
                <XAxis dataKey="label" {...axis} interval={2} />
                <YAxis {...axis} allowDecimals={false} width={30} />
                <Tooltip {...tip} formatter={(v) => [v, "Orders"]} />
                <Line type="monotone" dataKey="orders" stroke={c.gold} strokeWidth={2} dot={{ r: 3, fill: c.gold }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Top 5 by revenue"><TopList rows={d.top_by_revenue} metric="revenue" testid="top-by-revenue" /></Panel>
        <Panel title="Top 5 by units"><TopList rows={d.top_by_units} metric="units" testid="top-by-units" /></Panel>
      </div>
    </div>
  );
}
